import { z } from "zod";

import type {
  ActivityAdapter,
  ActivityCompletionMetrics,
  DomainSliceDefinition
} from "@/app/integration/contracts";
import {
  MATH_PATTERN_BRIDGE_LEVEL_ID,
  MATH_PATTERN_BRIDGE_MANIFEST,
  MATH_PATTERN_BRIDGE_PROMPT_AUDIO
} from "@/assets/requirements/math/math-pattern-bridge-manifest";
import {
  PATTERN_BRIDGE_LEVELS,
  type PatternBridgeLevel
} from "@/content/math";
import type { InputMethod } from "@/game/input";

const patternTokenSchema = z.enum([
  "acorn",
  "flower",
  "leaf",
  "mushroom",
  "shell",
  "star"
]);

const patternChoiceSchema = z.strictObject({
  id: z.string().trim().min(1).max(80),
  token: patternTokenSchema
});

export const mathPatternSequencePayloadSchema = z
  .strictObject({
    mode: z.literal("sequence"),
    repeatUnit: z.array(patternTokenSchema).min(2),
    sequence: z.array(patternTokenSchema.nullable()).min(2),
    choices: z.array(patternChoiceSchema).min(2),
    correctChoiceId: z.string().trim().min(1).max(80)
  })
  .superRefine((payload, context) => {
    const missingIndexes = payload.sequence.flatMap((token, index) =>
      token === null ? [index] : []
    );
    const choiceIds = payload.choices.map(({ id }) => id);
    const correctChoice = payload.choices.find(
      ({ id }) => id === payload.correctChoiceId
    );

    if (missingIndexes.length !== 1) {
      context.addIssue({
        code: "custom",
        path: ["sequence"],
        message: "A pattern sequence must contain exactly one empty position"
      });
    }

    if (new Set(payload.repeatUnit).size < 2) {
      context.addIssue({
        code: "custom",
        path: ["repeatUnit"],
        message: "A pattern must contain at least two different tokens"
      });
    }

    if (new Set(choiceIds).size !== choiceIds.length) {
      context.addIssue({
        code: "custom",
        path: ["choices"],
        message: "Pattern choice identifiers must be unique"
      });
    }

    if (!correctChoice) {
      context.addIssue({
        code: "custom",
        path: ["correctChoiceId"],
        message: "The correct pattern choice must reference a known choice"
      });
      return;
    }

    payload.sequence.forEach((token, index) => {
      const expectedToken =
        payload.repeatUnit[index % payload.repeatUnit.length];

      if (token !== null && token !== expectedToken) {
        context.addIssue({
          code: "custom",
          path: ["sequence", index],
          message: "The visible sequence must follow the repeat unit"
        });
      }
    });

    const missingIndex = missingIndexes[0];
    if (missingIndex === undefined) {
      return;
    }

    const expectedMissingToken =
      payload.repeatUnit[missingIndex % payload.repeatUnit.length];
    if (correctChoice.token !== expectedMissingToken) {
      context.addIssue({
        code: "custom",
        path: ["correctChoiceId"],
        message: "The correct choice must complete the repeat unit"
      });
    }
  });

export type MathPatternSequencePayload = z.infer<
  typeof mathPatternSequencePayloadSchema
>;
export type MathPatternToken = z.infer<typeof patternTokenSchema>;

export interface MathPatternBridgeState {
  readonly completed: boolean;
  readonly hintCount: number;
  readonly lastInputMethod: InputMethod | null;
  readonly retryCount: number;
  readonly selectedChoiceId: string | null;
}

export type MathPatternBridgeAction =
  | {
      readonly type: "select-choice";
      readonly choiceId: string;
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "request-hint" }
  | { readonly type: "reset" };

export interface MathPatternBridgeRenderItem {
  readonly empty: boolean;
  readonly id: string;
  readonly label: string;
  readonly position: number;
  readonly token: MathPatternToken | null;
}

export interface MathPatternBridgeRenderChoice {
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
  readonly token: MathPatternToken;
}

export interface MathPatternBridgeHintModel {
  readonly stage: "gentle-hint";
  readonly text: string;
}

export interface MathPatternBridgeResultModel {
  readonly heading: string;
  readonly message: string;
}

export interface MathPatternBridgeRenderModel {
  readonly choices: readonly MathPatternBridgeRenderChoice[];
  readonly hint: MathPatternBridgeHintModel | null;
  readonly items: readonly MathPatternBridgeRenderItem[];
  readonly prompt: string;
  readonly result: MathPatternBridgeResultModel | null;
}

export type MathPatternBridgeSequenceLevel = Omit<
  PatternBridgeLevel,
  "payload" | "promptAudio"
> & {
  readonly payload: MathPatternSequencePayload;
  readonly promptAudio: string;
};

const TOKEN_LABELS: Record<MathPatternToken, string> = {
  acorn: "橡果",
  flower: "花朵",
  leaf: "叶子",
  mushroom: "蘑菇",
  shell: "贝壳",
  star: "星星"
};

const MATH_PATTERN_BRIDGE_PROMPT = "空位该轮到哪个图案？";

function getCorrectChoice(
  payload: MathPatternSequencePayload
): MathPatternSequencePayload["choices"][number] {
  const correctChoice = payload.choices.find(
    ({ id }) => id === payload.correctChoiceId
  );

  if (!correctChoice) {
    throw new Error("Pattern bridge payload has no correct choice");
  }

  return correctChoice;
}

function createInitialState(): MathPatternBridgeState {
  return {
    completed: false,
    hintCount: 0,
    lastInputMethod: null,
    retryCount: 0,
    selectedChoiceId: null
  };
}

function reduceMathPatternBridge(
  state: MathPatternBridgeState,
  payload: MathPatternSequencePayload,
  action: MathPatternBridgeAction
): MathPatternBridgeState {
  if (action.type === "reset") {
    return createInitialState();
  }

  if (state.completed) {
    return state;
  }

  switch (action.type) {
    case "request-hint":
      return {
        ...state,
        hintCount: state.hintCount + 1
      };
    case "select-choice": {
      const selectedChoice = payload.choices.find(
        ({ id }) => id === action.choiceId
      );
      if (!selectedChoice) {
        return state;
      }

      const completed =
        selectedChoice.token === getCorrectChoice(payload).token;

      return {
        ...state,
        completed,
        lastInputMethod: action.inputMethod,
        retryCount: state.retryCount + (completed ? 0 : 1),
        selectedChoiceId: selectedChoice.id
      };
    }
  }
}

function getHintModel(
  state: MathPatternBridgeState,
  payload: MathPatternSequencePayload
): MathPatternBridgeHintModel | null {
  if (state.completed) {
    return null;
  }

  if (state.hintCount > 0) {
    return {
      stage: "gentle-hint",
      text:
        payload.repeatUnit.map((token) => TOKEN_LABELS[token]).join("、") +
        "在轮流排队。念一遍，空位该轮到谁？"
    };
  }

  if (state.selectedChoiceId !== null) {
    return {
      stage: "gentle-hint",
      text:
        "这个图案还没接上。把" +
        payload.sequence
          .flatMap((token) => (token === null ? [] : [TOKEN_LABELS[token]]))
          .join("、") +
        "轻轻念一遍，再选一个图案。"
    };
  }

  return null;
}

function getResolvedSequence(
  state: MathPatternBridgeState,
  payload: MathPatternSequencePayload
): readonly (MathPatternToken | null)[] {
  const correctToken = getCorrectChoice(payload).token;

  return payload.sequence.map((token) =>
    token === null && state.completed ? correctToken : token
  );
}

function toRenderModel(
  state: MathPatternBridgeState,
  payload: MathPatternSequencePayload
): MathPatternBridgeRenderModel {
  const resolvedSequence = getResolvedSequence(state, payload);

  return {
    prompt: MATH_PATTERN_BRIDGE_PROMPT,
    items: resolvedSequence.map((token, index) => ({
      empty: token === null,
      id: "pattern-slot-" + String(index + 1),
      label: token === null ? "空位" : TOKEN_LABELS[token],
      position: index + 1,
      token
    })),
    choices: payload.choices.map((choice) => ({
      ...choice,
      label: TOKEN_LABELS[choice.token],
      selected: state.selectedChoiceId === choice.id
    })),
    hint: getHintModel(state, payload),
    result: state.completed
      ? {
          heading: "图案小桥接好啦",
          message:
            resolvedSequence
              .flatMap((token) =>
                token === null ? [] : [TOKEN_LABELS[token]]
              )
              .join("、") + "，规律补完整了。"
        }
      : null
  };
}

function getMetrics(
  state: MathPatternBridgeState
): ActivityCompletionMetrics {
  const attemptCount = state.retryCount + (state.completed ? 1 : 0);
  const correctRate =
    state.completed && attemptCount > 0 ? 1 / attemptCount : 0;

  return {
    completed: state.completed,
    score: Math.round(correctRate * 100),
    correctRate,
    hintCount: state.hintCount,
    retryCount: state.retryCount
  };
}

export const MATH_PATTERN_BRIDGE_ADAPTER: ActivityAdapter<
  MathPatternSequencePayload,
  MathPatternBridgeState,
  MathPatternBridgeAction,
  MathPatternBridgeRenderModel
> = {
  activityType: "pattern-bridge",
  parsePayload: (value) => mathPatternSequencePayloadSchema.parse(value),
  createInitialState,
  reduce: (state, payload, action) =>
    reduceMathPatternBridge(state, payload, action),
  getMetrics: (state) => getMetrics(state),
  toRenderModel
};

function createMathPatternBridgeLevel(): MathPatternBridgeSequenceLevel {
  const sourceLevel = PATTERN_BRIDGE_LEVELS.find(
    ({ id }) => id === MATH_PATTERN_BRIDGE_LEVEL_ID
  );

  if (!sourceLevel) {
    throw new Error("Math pattern bridge level 1 is not registered");
  }

  if (sourceLevel.promptAudio !== MATH_PATTERN_BRIDGE_PROMPT_AUDIO) {
    throw new Error("Math pattern bridge narration does not match its manifest");
  }

  return {
    ...sourceLevel,
    promptAudio: sourceLevel.promptAudio,
    payload: MATH_PATTERN_BRIDGE_ADAPTER.parsePayload(sourceLevel.payload)
  };
}

export const MATH_PATTERN_BRIDGE_LEVEL = createMathPatternBridgeLevel();

export const MATH_PATTERN_BRIDGE_SLICE: DomainSliceDefinition<
  MathPatternSequencePayload,
  MathPatternBridgeState,
  MathPatternBridgeAction,
  MathPatternBridgeRenderModel
> = {
  domain: "math",
  levelId: MATH_PATTERN_BRIDGE_LEVEL.id,
  level: MATH_PATTERN_BRIDGE_LEVEL,
  adapter: MATH_PATTERN_BRIDGE_ADAPTER,
  assets: MATH_PATTERN_BRIDGE_MANIFEST
};
